/* The chat bar lives in a shadow root, so none of this touches the host page and the page's
   styles don't reach in. Colours follow the extension's side panel. */

:host {
  --bg: #0b0d12;
  --surface: #12151d;
  --surface-2: #181c26;
  --border: #242a38;
  --text: #e7eaf2;
  --muted: #8b94aa;
  --accent: #8b5cf6;
  --accent-2: #22d3ee;
  --accent-text: #c4b5fd;
  --ok: #34d399;
  --err: #f87171;
  --warn: #fbbf24;
  --user-bg: #2a2350;
  --code-bg: #0f1218;
  --shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  --panel-shadow: 0 18px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04);
  --font: 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif;
  --mono: 'Cascadia Code', Consolas, ui-monospace, 'SFMono-Regular', Menlo, monospace;
  --w: 400px; /* panel width, dragged by the visitor */
  --gap: 12px; /* space between the docked panel and the window edge */
  --ox: 20px; /* launcher offset from the side */
  --oy: 20px; /* launcher offset from the bottom */
  color-scheme: dark;
}

:host([theme='light']) {
  --bg: #f5f6fa;
  --surface: #ffffff;
  --surface-2: #eef0f6;
  --border: #dde1ea;
  --text: #1b1f2a;
  --muted: #616a80;
  --accent-text: #6d28d9;
  --ok: #059669;
  --err: #dc2626;
  --warn: #b45309;
  --user-bg: #ece7ff;
  --code-bg: #f0f2f7;
  --shadow: 0 8px 30px rgba(20, 25, 40, 0.1);
  --panel-shadow: 0 18px 60px rgba(20, 25, 40, 0.22), 0 0 0 1px rgba(20, 25, 40, 0.06);
  color-scheme: light;
}

@media (prefers-color-scheme: light) {
  :host([theme='auto']) {
    --bg: #f5f6fa;
    --surface: #ffffff;
    --surface-2: #eef0f6;
    --border: #dde1ea;
    --text: #1b1f2a;
    --muted: #616a80;
    --accent-text: #6d28d9;
    --ok: #059669;
    --err: #dc2626;
    --warn: #b45309;
    --user-bg: #ece7ff;
    --code-bg: #f0f2f7;
    --shadow: 0 8px 30px rgba(20, 25, 40, 0.1);
    --panel-shadow: 0 18px 60px rgba(20, 25, 40, 0.22), 0 0 0 1px rgba(20, 25, 40, 0.06);
    color-scheme: light;
  }
}

* {
  box-sizing: border-box;
}

[hidden] {
  display: none !important;
}

button,
input,
select,
textarea {
  font: inherit;
  color: inherit;
}

code {
  font-family: var(--mono);
  font-size: 0.9em;
  background: var(--code-bg);
  border: 1px solid var(--border);
  border-radius: 5px;
  padding: 0 4px;
}

:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}

/* ------------------------------ the visible area ------------------------------ */

/* Everything is placed inside .vp, which JS keeps over the visual viewport: the part of the page
   on screen. On a phone that is not the layout viewport when a page is wider than the screen (the
   browser then widens the layout viewport, and position: fixed and @media follow it) or when the
   on-screen keyboard is up. Sizes inside follow .vp through container queries and units. */
.vp {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  transform-origin: 0 0;
  container-type: size;
  overflow: hidden;
  pointer-events: none;
}

/* ------------------------------ launcher ------------------------------ */

.launcher {
  position: absolute;
  pointer-events: auto;
  bottom: calc(var(--oy) + env(safe-area-inset-bottom, 0px));
  right: var(--ox);
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 18px;
  background: transparent;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(10, 12, 20, 0.35), 0 2px 6px rgba(10, 12, 20, 0.25);
  transition: transform 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

:host([side='left']) .launcher {
  right: auto;
  left: var(--ox);
}

.launcher svg {
  display: block;
  width: 100%;
  height: 100%;
}

.launcher:hover {
  transform: translateY(-2px) scale(1.03);
  box-shadow: 0 14px 36px rgba(10, 12, 20, 0.4), 0 2px 6px rgba(10, 12, 20, 0.25);
}

.launcher:active {
  transform: scale(0.97);
}

:host([open]) .launcher {
  opacity: 0;
  transform: scale(0.6);
  pointer-events: none;
}

/* a reply arrived while the panel was closed */
.launcher .badge {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--err);
  border: 2px solid #fff;
}

/* working while the panel is closed */
.launcher.busy::after {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 22px;
  border: 2px solid transparent;
  border-top-color: var(--accent-2);
  border-right-color: var(--accent);
  animation: spin 1s linear infinite;
}

/* ------------------------------ panel ------------------------------ */

.panel {
  position: absolute;
  pointer-events: auto;
  top: var(--gap);
  bottom: var(--gap);
  right: var(--gap);
  width: min(var(--w), calc(100cqw - 2 * var(--gap)));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg);
  color: var(--text);
  font: 14px/1.5 var(--font);
  text-align: left;
  border-radius: 16px;
  box-shadow: var(--panel-shadow);
  transform: translateX(calc(100% + var(--gap) + 24px));
  visibility: hidden;
  transition: transform 0.26s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s linear 0.26s;
  -webkit-font-smoothing: antialiased;
}

:host([side='left']) .panel {
  right: auto;
  left: var(--gap);
  transform: translateX(calc(-100% - var(--gap) - 24px));
}

:host([open]) .panel {
  transform: none;
  visibility: visible;
  transition: transform 0.26s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s;
}

:host([dragging]) .panel {
  transition: none;
  user-select: none;
}

/* drag the inner edge to resize */
.resize {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
  cursor: ew-resize;
  z-index: 2;
  touch-action: none;
}

:host([side='left']) .resize {
  left: auto;
  right: 0;
}

.resize::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 2px;
  width: 3px;
  height: 36px;
  margin-top: -18px;
  border-radius: 2px;
  background: var(--border);
  opacity: 0;
  transition: opacity 0.15s;
}

.resize:hover::after,
:host([dragging]) .resize::after {
  opacity: 1;
}

.grab,
.backdrop {
  display: none;
}

/* ------------------------------ phones: a bottom sheet ------------------------------ */

@container (max-width: 640px) {
  .launcher {
    width: 52px;
    height: 52px;
    border-radius: 16px;
  }

  .backdrop {
    display: block;
    position: absolute;
    inset: 0;
    background: rgba(5, 7, 12, 0.45);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.22s;
    touch-action: none;
  }

  :host([open]) .backdrop {
    opacity: 1;
    pointer-events: auto;
  }

  .panel,
  :host([side='left']) .panel {
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 88cqh;
    max-height: calc(100cqh - 8px);
    border-radius: 18px 18px 0 0;
    transform: translateY(105%);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  :host([open]) .panel,
  :host([open][side='left']) .panel {
    transform: translateY(var(--pull, 0px));
  }

  :host([kb]) .panel {
    padding-bottom: 0;
  }

  .resize {
    display: none;
  }

  .grab {
    display: block;
    flex: none;
    height: 18px;
    margin-bottom: -10px;
    position: relative;
    z-index: 1;
    touch-action: none;
    cursor: grab;
  }

  .grab::before {
    content: '';
    position: absolute;
    top: 7px;
    left: 50%;
    width: 38px;
    height: 4px;
    margin-left: -19px;
    border-radius: 2px;
    background: var(--border);
  }

  .top {
    touch-action: none;
  }

  /* iOS zooms into inputs under 16px */
  .composer textarea,
  input[type='text'],
  input[type='password'],
  input[type='url'],
  input[type='number'],
  select {
    font-size: 16px;
  }
}

/* short screens (a phone on its side, or the keyboard up): the sheet takes the height */
@container (max-width: 640px) and (max-height: 520px) {
  .panel,
  :host([side='left']) .panel {
    height: calc(100cqh - 4px);
    border-radius: 12px 12px 0 0;
  }

  .grab {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .panel,
  .launcher,
  .backdrop {
    transition: none !important;
  }
}

/* ------------------------------ header ------------------------------ */

.top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  flex: none;
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 1;
}

.brand .mark {
  width: 26px;
  height: 26px;
  flex: none;
}

.brand .mark svg {
  display: block;
  width: 100%;
  height: 100%;
}

.titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}

.titles .name {
  font-weight: 650;
  letter-spacing: 0.01em;
}

.titles .site {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.titles .site .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--muted);
  flex: none;
}

.titles .site.on .dot {
  background: var(--ok);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--ok) 25%, transparent);
}

.actions {
  display: flex;
  gap: 2px;
  flex: none;
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}

.icon-btn:hover {
  background: var(--surface-2);
  color: var(--text);
}

/* ------------------------------ views ------------------------------ */

.view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.scroll {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 14px 8px;
  scrollbar-width: thin;
}

/* ------------------------------ thread ------------------------------ */

.thread {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.msg-user {
  align-self: flex-end;
  max-width: 88%;
  background: var(--user-bg);
  padding: 8px 12px;
  border-radius: 14px 14px 4px 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.msg-assistant {
  overflow-wrap: anywhere;
}

.msg-assistant > :first-child {
  margin-top: 0;
}

.msg-assistant > :last-child {
  margin-bottom: 0;
}

.msg-assistant p {
  margin: 0 0 8px;
}

.msg-assistant ul,
.msg-assistant ol {
  margin: 0 0 8px;
  padding-left: 20px;
}

.msg-assistant h3,
.msg-assistant h4 {
  margin: 10px 0 6px;
  font-size: 14px;
}

.msg-assistant pre {
  background: var(--code-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 10px;
  overflow-x: auto;
  margin: 0 0 8px;
}

.msg-assistant pre code {
  background: none;
  border: 0;
  padding: 0;
}

.msg-assistant a {
  color: var(--accent-text);
}

.msg-assistant.streaming::after {
  content: '▍';
  color: var(--accent-text);
  animation: blink 1s steps(2) infinite;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.tools {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tool {
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface);
  font-size: 12.5px;
}

.tool summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 9px;
  cursor: pointer;
  list-style: none;
  color: var(--muted);
}

.tool summary::-webkit-details-marker {
  display: none;
}

.tool summary .label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.tool .state {
  width: 14px;
  height: 14px;
  flex: none;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
}

.tool.ok .state {
  color: var(--ok);
}

.tool.err {
  border-color: color-mix(in srgb, var(--err) 45%, var(--border));
}

.tool.err .state {
  color: var(--err);
}

.tool .chev {
  transition: transform 0.15s;
}

.tool[open] .chev {
  transform: rotate(90deg);
}

.tool pre {
  margin: 0;
  padding: 8px 10px;
  border-top: 1px solid var(--border);
  font: 11.5px/1.45 var(--mono);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 260px;
  overflow-y: auto;
  color: var(--muted);
}

.tool pre b {
  color: var(--text);
  font-weight: 600;
}

.link-chip {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 6px 11px;
  border: 1px solid color-mix(in srgb, var(--accent) 55%, var(--border));
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 12%, var(--surface));
  color: var(--text);
  font-size: 13px;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-chip:hover {
  border-color: var(--accent);
}

.error-box {
  border: 1px solid color-mix(in srgb, var(--err) 50%, var(--border));
  background: color-mix(in srgb, var(--err) 10%, var(--surface));
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.notice {
  font-size: 12.5px;
  color: var(--muted);
  text-align: center;
}

/* ------------------------------ empty state ------------------------------ */

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 22px 6px 10px;
}

.empty .hero {
  width: 52px;
  height: 52px;
  filter: drop-shadow(0 10px 24px color-mix(in srgb, var(--accent) 40%, transparent));
}

.empty .hero svg {
  display: block;
  width: 100%;
  height: 100%;
}

.empty h2 {
  margin: 4px 0 0;
  font-size: 17px;
  line-height: 1.3;
}

.empty p {
  margin: 0;
  color: var(--muted);
  max-width: 320px;
}

.empty .fine {
  font-size: 11.5px;
  margin-top: 6px;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
}

.suggestion {
  text-align: left;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  padding: 8px 11px;
  cursor: pointer;
  font-size: 13px;
}

.suggestion:hover {
  border-color: var(--accent);
}

.loading {
  display: grid;
  place-items: center;
  padding: 40px 0;
}

/* ------------------------------ status + composer ------------------------------ */

.status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 16px 6px;
  font-size: 12px;
  color: var(--muted);
}

.spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--border);
  border-top-color: var(--accent-2);
  animation: spin 0.8s linear infinite;
  flex: none;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.composer {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin: 0 12px 12px;
  padding: 6px 6px 6px 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow);
  flex: none;
}

.composer:focus-within {
  border-color: color-mix(in srgb, var(--accent) 70%, var(--border));
}

.composer textarea {
  flex: 1;
  min-width: 0;
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  padding: 6px 0;
  max-height: 180px;
  line-height: 1.45;
}

.composer textarea::placeholder {
  color: var(--muted);
}

.composer textarea:disabled {
  cursor: not-allowed;
}

.send,
.stop {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 0;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 55%, var(--accent-2)));
}

.send:disabled {
  opacity: 0.4;
  cursor: default;
}

.stop {
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
}

/* ------------------------------ voice ------------------------------ */

.mic {
  --level: 0;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--border);
  display: grid;
  place-items: center;
  cursor: pointer;
  background: var(--surface-2);
  color: var(--muted);
  touch-action: none;
  transition: box-shadow 0.06s linear;
  -webkit-user-select: none;
  user-select: none;
}

.mic:hover:not(:disabled) {
  color: var(--text);
}

.mic:disabled {
  opacity: 0.4;
  cursor: default;
}

.mic > * {
  grid-area: 1 / 1;
}

.mic .spinner {
  display: none;
}

.mic.starting {
  color: var(--err);
  border-color: var(--err);
  animation: pulse 0.9s ease-in-out infinite;
}

.mic.recording {
  color: #fff;
  border-color: transparent;
  background: var(--err);
  box-shadow: 0 0 0 calc(2px + var(--level) * 9px) color-mix(in srgb, var(--err) 28%, transparent);
}

.mic.busy .spinner {
  display: block;
}

.mic.busy .mic-glyph {
  visibility: hidden;
}

.voice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 6px;
  padding: 5px 6px 5px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  font-size: 12.5px;
  color: var(--muted);
}

.voice.err {
  color: var(--text);
  border-color: color-mix(in srgb, var(--err) 50%, var(--border));
  background: color-mix(in srgb, var(--err) 10%, var(--surface));
}

.voice-icon {
  flex: none;
  width: 10px;
  height: 10px;
  display: grid;
  place-items: center;
}

.voice.listen .voice-icon {
  border-radius: 50%;
  background: var(--err);
  animation: pulse 1.1s ease-in-out infinite;
}

.voice.busy .voice-icon,
.voice.speak .voice-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--border);
  border-top-color: var(--accent-2);
  animation: spin 0.8s linear infinite;
}

.voice.speak .voice-icon {
  border: 0;
  border-radius: 2px;
  background: linear-gradient(var(--accent-2), var(--accent-2)) 0 50% / 2px 100% no-repeat, linear-gradient(var(--accent-2), var(--accent-2)) 50% 50% / 2px 60% no-repeat,
    linear-gradient(var(--accent-2), var(--accent-2)) 100% 50% / 2px 80% no-repeat;
  animation: talk 0.9s ease-in-out infinite alternate;
}

.voice.info .voice-icon,
.voice.err .voice-icon {
  display: none;
}

.voice-text {
  flex: 1;
  min-width: 0;
}

.voice-action {
  flex: none;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--surface-2);
  padding: 2px 9px;
  font-size: 12px;
  cursor: pointer;
}

.voice-action:hover {
  border-color: var(--accent);
}

@keyframes pulse {
  50% {
    opacity: 0.35;
  }
}

@keyframes talk {
  to {
    transform: scaleY(0.45);
  }
}

/* ------------------------------ settings ------------------------------ */

.settings h1 {
  font-size: 18px;
  margin: 0 0 6px;
}

.settings h2 {
  font-size: 14px;
  margin: 22px 0 8px;
}

.muted {
  color: var(--muted);
}

.settings p.muted {
  margin: 0 0 16px;
  font-size: 13px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 14px;
}

.field > span {
  font-weight: 600;
  font-size: 13px;
}

.field small {
  font-size: 12px;
}

.row {
  display: flex;
  gap: 6px;
}

input[type='text'],
input[type='password'],
input[type='url'],
input[type='number'],
select {
  flex: 1;
  min-width: 0;
  padding: 7px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  outline: 0;
}

input:focus,
select:focus {
  border-color: var(--accent);
}

.btn {
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  cursor: pointer;
  white-space: nowrap;
}

.btn.primary {
  border-color: transparent;
  color: #fff;
  background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 55%, var(--accent-2)));
}

.btn.ghost {
  background: transparent;
}

.btn:hover {
  filter: brightness(1.1);
}

.btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.check {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 14px;
  cursor: pointer;
}

.check input {
  margin-top: 3px;
  accent-color: var(--accent);
}

.check span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
}

.check small {
  font-size: 12px;
}

.voice-opts {
  margin: -4px 0 16px 6px;
  padding-left: 16px;
  border-left: 2px solid var(--border);
}

.field-pair {
  display: flex;
  flex-wrap: wrap;
  gap: 0 10px;
}

.field-pair > .field {
  flex: 1 1 150px;
  min-width: 0;
}

.field-pair > .field.narrow {
  flex: 0 1 90px;
}

.voice-opts .hint {
  display: block;
  margin: -8px 0 14px;
  font-size: 12px;
}

.mic-state {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 12.5px;
  color: var(--muted);
}

.mic-state.ok {
  color: var(--ok);
}

.mic-state.err {
  color: var(--err);
}

.mic-state .btn {
  padding: 3px 10px;
  font-size: 12px;
}

.field small a,
.settings p.muted a {
  color: var(--accent-text);
}

.advanced {
  margin-bottom: 12px;
}

.advanced summary {
  cursor: pointer;
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 10px;
}

.need-key {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 6px;
  padding: 12px;
  border: 1px dashed color-mix(in srgb, var(--accent) 55%, var(--border));
  border-radius: 12px;
}

.need-key p {
  margin: 0;
  font-size: 13px;
  color: var(--text);
}

.form-msg {
  min-height: 20px;
  font-size: 13px;
  margin-bottom: 8px;
}

.form-msg.ok {
  color: var(--ok);
}

.form-msg.err {
  color: var(--err);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.about {
  margin-top: 22px;
  font-size: 12px;
}
